import React, { useState } from 'react';
import { Plus, Search, Edit2, Trash2, DollarSign, History, Calendar, Building2, FileText, Download, XCircle, Tag } from 'lucide-react';
import { apiService } from '../services/api';
import { Capex } from '../types';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import { useLanguage } from '../contexts/LanguageContext';

const CapexPage: React.FC = () => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  useGlobalSearch('capex', setSearchTerm);
  const [showModal, setShowModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editingCapex, setEditingCapex] = useState<Capex | null>(null);
  const [selectedCapex, setSelectedCapex] = useState<Capex | null>(null);
  const [filterYear, setFilterYear] = useState<number>(new Date().getFullYear());
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCompany, setFilterCompany] = useState<number>(0);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [capexData, setCapexData] = useState<Capex[]>([]);
  const [companiesData, setCompaniesData] = useState<any[]>([]);
  const [ordersData, setOrdersData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [capexOrders, setCapexOrders] = useState<any[]>([]);
  const [capexTransactions, setCapexTransactions] = useState<any[]>([]);
  const [categoriesData, setCategoriesData] = useState<{id: number; name: string}[]>([]);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);

  const [capexFormData, setCapexFormData] = useState({
    capex_number: '',
    company_id: 0,
    year: new Date().getFullYear(),
    category: '',
    total_amount: 0,
    currency: 'EUR' as 'TRY' | 'USD' | 'EUR',
    description: ''
  });

  // Load data on component mount
  React.useEffect(() => {
    loadData();
  }, []);

  // Bildirimden belirli bir CAPEX açma
  React.useEffect(() => {
    const handler = async (e: CustomEvent) => {
      if (e.detail?.page !== 'capex' || !e.detail?.entityId) return;
      try {
        const capex = await apiService.getCapexById(e.detail.entityId);
        if (capex) showHistory(capex);
      } catch (err) { console.error('[openEntityById capex]', err); }
    };
    window.addEventListener('openEntityById', handler as EventListener);
    return () => window.removeEventListener('openEntityById', handler as EventListener);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const [capexResponse, companiesResponse, ordersResponse, categoriesResponse] = await Promise.all([
        apiService.getCapex(),
        apiService.getCompanies(),
        fetch('/api/orders', {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(res => res.json()),
        fetch('/api/capex/categories', {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(res => res.json())
      ]);
      setCapexData(Array.isArray(capexResponse) ? capexResponse : []);
      setCompaniesData(Array.isArray(companiesResponse) ? companiesResponse : []);
      setOrdersData(Array.isArray(ordersResponse) ? ordersResponse : []);
      setCategoriesData(Array.isArray(categoriesResponse) ? categoriesResponse : []);
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(t('common.error'), t('capex.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/capex/categories', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCategoriesData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    setSavingCategory(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/capex/categories', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newCategoryName.trim() })
      });
      if (!response.ok) {
        const err = await response.json();
        showGlobalNotification.error(t('common.error'), err.error || t('capex.errorCategoryAdd'));
        return;
      }
      const created = await response.json();
      await loadCategories();
      setCapexFormData(prev => ({ ...prev, category: created.name }));
      setNewCategoryName('');
      setShowNewCategoryInput(false);
      showGlobalNotification.success(t('common.success'), t('capex.successCategoryAdded'));
    } catch (error) {
      showGlobalNotification.error(t('common.error'), t('capex.errorCategoryAddGeneral'));
    } finally {
      setSavingCategory(false);
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i + 1);

  const filteredCapex = capexData.filter(capex => {
    const searchLower = String(searchTerm || '').toLowerCase();
    const matchesSearch = (capex.capex_number || '').toLowerCase().includes(searchLower) ||
                         (capex.category || '').toLowerCase().includes(searchLower) ||
                         (capex.company?.name || '').toLowerCase().includes(searchLower);
    const matchesYear = filterYear === 0 || capex.year === filterYear;
    const matchesStatus = filterStatus === 'all' || capex.status === filterStatus;
    const matchesCompany = filterCompany === 0 || capex.company_id === filterCompany;
    const matchesCategory = filterCategory === 'all' || capex.category === filterCategory;

    return matchesSearch && matchesYear && matchesStatus && matchesCompany && matchesCategory;
  });

  const handleEdit = (capex: Capex) => {
    setEditingCapex(capex);
    setCapexFormData({
      capex_number: capex.capex_number,
      company_id: capex.company_id,
      year: capex.year,
      category: capex.category,
      total_amount: capex.total_amount,
      currency: capex.currency,
      description: capex.description || ''
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingCapex(null);
    const nextNumber = `CAPEX-${new Date().getFullYear()}-${String(capexData.length + 1).padStart(3, '0')}`;
    setCapexFormData({
      capex_number: nextNumber,
      company_id: 0,
      year: new Date().getFullYear(),
      category: '',
      total_amount: 0,
      currency: 'EUR',
      description: ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!capexFormData.capex_number || !capexFormData.company_id || !capexFormData.category) {
      showGlobalNotification.error(t('common.error'), t('capex.errorRequiredFields'));
      return;
    }
    
    try {
      if (editingCapex) {
        // Update existing capex
        await apiService.updateCapex(editingCapex.id, capexFormData);
        showGlobalNotification.success(t('common.success'), t('capex.successUpdated'));
      } else {
        // Add new capex
        await apiService.createCapex(capexFormData);
        showGlobalNotification.success(t('common.success'), t('capex.successCreated'));
      }
      
      // Reload data
      await loadData();
      setShowModal(false);
      setShowNewCategoryInput(false);
      setNewCategoryName('');
      setCapexFormData({
        capex_number: '',
        company_id: 0,
        year: new Date().getFullYear(),
        category: '',
        total_amount: 0,
        currency: 'TRY',
        description: ''
      });
    } catch (error: any) {
      console.error('Error saving CAPEX:', error);
      showGlobalNotification.error(t('common.error'), error.message || t('capex.errorSave'));
    }
  };

  const clearBlockedAmount = async (id: number) => {
    if (confirm(t('capex.confirmClearBlocked'))) {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`/api/capex/${id}/clear-blocked`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          await loadData();
          showGlobalNotification.success(t('common.success'), t('capex.blockedCleared'));
        } else {
          const error = await response.json();
          
          if (error.activeOrders && error.activeOrders.length > 0) {
            const orderNumbers = error.activeOrders.map((o: any) => o.order_number).join(', ');
            const shouldCancel = confirm(
              t('capex.confirmCancelOrders', { orders: orderNumbers })
            );
            
            if (shouldCancel) {
              // Get full order details and cancel them
              for (const orderInfo of error.activeOrders) {
                try {
                  // Find the full order from ordersData
                  const fullOrder = ordersData.find(o => o.order_number === orderInfo.order_number);
                  if (fullOrder) {
                    await fetch(`/api/orders/${fullOrder.id}`, {
                      method: 'PUT',
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({ 
                        ...fullOrder, 
                        status: 'iptal-edildi',
                        beam_order_number: fullOrder.beam_order_number || ''
                      })
                    });
                  }
                } catch (cancelError) {
                  console.error('Error cancelling order:', cancelError);
                }
              }
              
              // Try clearing blocked amount again
              const retryResponse = await fetch(`/api/capex/${id}/clear-blocked`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (retryResponse.ok) {
                await loadData();
                showGlobalNotification.success(t('common.success'), t('capex.ordersAndBlockedCleared'));
              }
            }
          } else {
            showGlobalNotification.error(t('common.error'), error.message || t('capex.errorClearBlocked'));
          }
        }
      } catch (error: any) {
        console.error('Error clearing blocked amount:', error);
        showGlobalNotification.error(t('common.error'), t('capex.errorClearBlocked'));
      }
    }
  };

  const handleDelete = (id: number) => {
    if (confirm(t('capex.confirmDelete'))) {
    }
  };

  const showHistory = async (capex: Capex) => {
    setSelectedCapex(capex);
    setShowHistoryModal(true);
    setCapexOrders([]);
    setCapexTransactions([]);

    // Load orders and transactions for this CAPEX in parallel
    try {
      const [orders, transactions] = await Promise.all([
        apiService.getCapexOrders(capex.id),
        apiService.getCapexTransactions(capex.id)
      ]);
      setCapexOrders(Array.isArray(orders) ? orders : []);
      setCapexTransactions(Array.isArray(transactions) ? transactions : []);
    } catch (error) {
      console.error('Error loading CAPEX history:', error);
      showGlobalNotification.error(t('common.error'), t('capex.errorLoadOrders'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-primary/20 text-primary-dark';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return t('capex.statusActive');
      case 'completed':
        return t('capex.statusCompleted');
      case 'cancelled':
        return t('capex.statusCancelled');
      default:
        return t('capex.statusUnknown');
    }
  };

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'USD':
        return '$';
      case 'EUR':
        return '€';
      case 'TRY':
        return '₺';
      default:
        return '€'; // Default to EUR since most CAPEX data is in EUR
    }
  };

  const getUsagePercentage = (capex: Capex) => {
    return (capex.used_amount / capex.total_amount) * 100;
  };

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case 'siparis-olusturuldu':
        return 'bg-primary/20 text-primary-dark';
      case 'beklemede':
        return 'bg-yellow-100 text-yellow-800';
      case 'onaylandi':
        return 'bg-green-100 text-green-800';
      case 'siparis-verildi':
        return 'bg-indigo-100 text-indigo-800';
      case 'teslim-edildi':
        return 'bg-purple-100 text-purple-800';
      case 'tamamlandi':
        return 'bg-emerald-100 text-emerald-800';
      case 'iptal-edildi':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getOrderStatusText = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'siparis-olusturuldu': t('capex.orderStatusCreated'),
      'beklemede': t('capex.orderStatusPending'),
      'onaylandi': t('capex.orderStatusApproved'),
      'siparis-verildi': t('capex.orderStatusOrdered'),
      'teslim-edildi': t('capex.orderStatusDelivered'),
      'tamamlandi': t('capex.orderStatusCompleted'),
      'iptal-edildi': t('capex.orderStatusCancelled')
    };
    return statusMap[status] || status;
  };

  const exportToPDF = () => {
    const printContent = `
      <html>
        <head>
          <title>${t('capex.pdfTitle')}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .header { text-align: center; margin-bottom: 30px; }
            .summary { margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${t('capex.pdfTitle')}</h1>
            <p>${t('capex.pdfDate')} ${new Date().toLocaleDateString('tr-TR')}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>${t('capex.pdfColCapexNo')}</th>
                <th>${t('capex.pdfColCompany')}</th>
                <th>${t('capex.pdfColCategory')}</th>
                <th>${t('capex.pdfColYear')}</th>
                <th>${t('capex.pdfColTotalBudget')}</th>
                <th>${t('capex.pdfColUsed')}</th>
                <th>${t('capex.pdfColRemaining')}</th>
                <th>${t('capex.pdfColStatus')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredCapex.map(capex => `
                <tr>
                  <td>${capex.capex_number}</td>
                  <td>${capex.company?.name || '-'}</td>
                  <td>${capex.category}</td>
                  <td>${capex.year}</td>
                  <td>${getCurrencySymbol(capex.currency)}${(capex.total_amount || 0).toLocaleString()}</td>
                  <td>${getCurrencySymbol(capex.currency)}${(capex.used_amount || 0).toLocaleString()}</td>
                  <td>${getCurrencySymbol(capex.currency)}${(capex.remaining_amount || 0).toLocaleString()}</td>
                  <td>${getStatusText(capex.status)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    }
    showGlobalNotification.success(t('common.success'), t('capex.pdfReady'));
  };

  const exportToExcel = () => {
    const headers = [t('capex.pdfColCapexNo'), t('capex.pdfColCompany'), 'Bölge', t('capex.pdfColCategory'), t('capex.pdfColYear'), t('capex.fieldCurrency'), t('capex.pdfColTotalBudget'), t('capex.used'), t('capex.remaining'), 'Kullanım Oranı (%)', t('capex.pdfColStatus'), t('capex.fieldDescription'), t('common.date')];
    
    const data = filteredCapex.map(capex => [
      capex.capex_number,
      capex.company?.name || '-',
      capex.region || '-',
      capex.category,
      capex.year.toString(),
      capex.currency,
      capex.total_amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      capex.used_amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      capex.remaining_amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      ((capex.used_amount / capex.total_amount) * 100).toFixed(1),
      getStatusText(capex.status),
      capex.description || '-',
      new Date(capex.created_at).toLocaleDateString('tr-TR')
    ]);
    
    // BOM ekleyerek UTF-8 desteği sağla
    const BOM = '\uFEFF';
    const csvContent = BOM + [headers, ...data]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `CAPEX-Raporu-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showGlobalNotification.success(t('common.success'), t('capex.excelDownloaded'));
  };

  const totalBudget = filteredCapex.reduce((sum, capex) => sum + capex.total_amount, 0);
  const totalUsed = filteredCapex.reduce((sum, capex) => sum + capex.used_amount, 0);
  const totalRemaining = filteredCapex.reduce((sum, capex) => sum + capex.remaining_amount, 0);

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('capex.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{t('capex.subtitle')}</p>
        </div>
        <div className="grid grid-cols-1 sm:flex sm:space-x-3 gap-2 w-full sm:w-auto">
          <button
            onClick={exportToPDF}
            className="flex items-center justify-center px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px] text-sm"
          >
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </button>
          <button
            onClick={exportToExcel}
            className="flex items-center justify-center px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors min-h-[44px] text-sm"
          >
            <Download className="w-4 h-4 mr-2" />
            Excel
          </button>
          <button
            onClick={handleAdd}
            className="flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors min-h-[44px] text-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('capex.newCapex')}
          </button>
        </div>
      </div>



      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="relative sm:col-span-2 lg:col-span-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={t('capex.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-full min-h-[44px]"
          />
        </div>

        <select
          value={filterYear}
          onChange={(e) => setFilterYear(parseInt(e.target.value))}
          className="px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[44px]"
        >
          <option value={0}>{t('capex.allYears')}</option>
          {years.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>

        <select
          value={filterCompany}
          onChange={(e) => setFilterCompany(parseInt(e.target.value))}
          className="px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[44px]"
        >
          <option value={0}>{t('capex.allCompanies')}</option>
          {companiesData.map(company => (
            <option key={company.id} value={company.id}>{company.name}</option>
          ))}
        </select>

        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[44px]"
        >
          <option value="all">{t('capex.allCategories')}</option>
          {categoriesData.map(cat => (
            <option key={cat.id} value={cat.name}>{cat.name}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[44px]"
        >
          <option value="all">{t('capex.allStatuses')}</option>
          <option value="active">{t('capex.statusActive')}</option>
          <option value="completed">{t('capex.statusCompleted')}</option>
          <option value="cancelled">{t('capex.statusCancelled')}</option>
        </select>
      </div>

      {/* CAPEX Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {filteredCapex.map((capex) => {
          const usagePercentage = getUsagePercentage(capex);
          return (
            <div
              key={capex.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col sm:flex-row justify-between items-start mb-4 gap-3">
                <div className="flex items-center flex-1 min-w-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                  </div>
                  <div className="ml-3 min-w-0 flex-1">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {capex.capex_number}
                    </h3>
                    <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-md text-xs font-medium bg-green-50 text-green-700 border border-green-200 truncate max-w-full">
                      <Tag className="w-3 h-3 mr-1 flex-shrink-0" />
                      {capex.category}
                    </span>
                  </div>
                </div>
                <div className="flex space-x-1 sm:space-x-2">
                  <button
                    onClick={() => showHistory(capex)}
                    className="p-1 min-h-[32px] min-w-[32px] flex items-center justify-center text-gray-400 hover:text-primary transition-colors"
                    title={t('capex.viewHistory')}
                  >
                    <History className="w-3 h-3 sm:w-4 sm:h-4" />
                  </button>
                  {capex.blocked_amount > 0 && (
                    <button
                      onClick={() => clearBlockedAmount(capex.id)}
                      className="p-1 min-h-[32px] min-w-[32px] flex items-center justify-center text-gray-400 hover:text-orange-600 transition-colors"
                      title={t('capex.clearBlocked')}
                    >
                      <XCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(capex)}
                    className="p-1 min-h-[32px] min-w-[32px] flex items-center justify-center text-gray-400 hover:text-primary transition-colors"
                  >
                    <Edit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(capex.id)}
                    className="p-1 min-h-[32px] min-w-[32px] flex items-center justify-center text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">{capex.company?.name}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">{capex.year}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('capex.budgetUsage')}</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {usagePercentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 relative">
                    {/* Used amount */}
                    <div
                      className={`h-2 rounded-l-full transition-all duration-300 absolute left-0 ${
                        usagePercentage > 90 ? 'bg-red-500' :
                        usagePercentage > 70 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                    />
                    {/* Blocked amount */}
                    {capex.blocked_amount > 0 && (
                      <div
                        className="h-2 bg-orange-400 transition-all duration-300 absolute"
                        style={{ 
                          left: `${Math.min(usagePercentage, 100)}%`,
                          width: `${Math.min((capex.blocked_amount / capex.total_amount) * 100, 100 - usagePercentage)}%`
                        }}
                        title={t('capex.blockedAmount', { symbol: getCurrencySymbol(capex.currency), amount: (capex.blocked_amount || 0).toLocaleString() })}
                      />
                    )}
                  </div>
                  {capex.blocked_amount > 0 && (
                    <div className="text-xs text-orange-600">
                      {t('capex.blockedAmount', { symbol: getCurrencySymbol(capex.currency), amount: (capex.blocked_amount || 0).toLocaleString() })}
                    </div>
                  )}
                </div>

                <div className={`grid ${capex.blocked_amount > 0 ? 'grid-cols-4' : 'grid-cols-3'} gap-4 pt-4 border-t border-gray-100`}>
                  <div className="text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('capex.total')}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {getCurrencySymbol(capex.currency)}{(capex.total_amount || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('capex.used')}</p>
                    <p className="text-sm font-semibold text-red-600">
                      {getCurrencySymbol(capex.currency)}{(capex.used_amount || 0).toLocaleString()}
                    </p>
                  </div>
                  {capex.blocked_amount > 0 && (
                    <div className="text-center">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('capex.blocked')}</p>
                      <p className="text-sm font-semibold text-orange-600">
                        {getCurrencySymbol(capex.currency)}{(capex.blocked_amount || 0).toLocaleString()}
                      </p>
                    </div>
                  )}
                  <div className="text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('capex.remaining')}</p>
                    <p className="text-sm font-semibold text-green-600">
                      {getCurrencySymbol(capex.currency)}{(capex.remaining_amount || 0).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(capex.status)}`}>
                    {getStatusText(capex.status)}
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(capex.created_at).toLocaleDateString('tr-TR')}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* CAPEX Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingCapex ? t('capex.modalTitleEdit') : t('capex.modalTitleNew')}
              </h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('capex.fieldCapexNumber')}
                  </label>
                  <input
                    type="text"
                    value={capexFormData.capex_number}
                    onChange={(e) => setCapexFormData({...capexFormData, capex_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="CAPEX-2024-001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('capex.fieldCompany')}
                  </label>
                  <select
                    value={capexFormData.company_id}
                    onChange={(e) => setCapexFormData({...capexFormData, company_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value={0}>{t('capex.selectCompany')}</option>
                    {companiesData.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('capex.fieldYear')}
                  </label>
                  <select
                    value={capexFormData.year}
                    onChange={(e) => setCapexFormData({...capexFormData, year: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('capex.fieldCategory')}
                    </label>
                    <button
                      type="button"
                      onClick={() => { setShowNewCategoryInput(v => !v); setNewCategoryName(''); }}
                      className="flex items-center text-xs text-green-600 hover:text-green-700"
                    >
                      <Tag className="w-3 h-3 mr-1" />
                      {t('capex.addNewCategory')}
                    </button>
                  </div>
                  <select
                    value={capexFormData.category}
                    onChange={(e) => setCapexFormData({...capexFormData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">{t('capex.selectCategory')}</option>
                    {categoriesData.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  {showNewCategoryInput && (
                    <div className="mt-2 flex gap-2">
                      <input
                        type="text"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCategory(); } }}
                        placeholder={t('capex.newCategoryPlaceholder')}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={handleAddCategory}
                        disabled={savingCategory || !newCategoryName.trim()}
                        className="px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {savingCategory ? '...' : t('capex.addCategoryBtn')}
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('capex.fieldTotalAmount')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={capexFormData.total_amount}
                    onChange={(e) => setCapexFormData({...capexFormData, total_amount: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="150000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('capex.fieldCurrency')}
                  </label>
                  <select
                    value={capexFormData.currency}
                    onChange={(e) => setCapexFormData({...capexFormData, currency: e.target.value as 'TRY' | 'USD' | 'EUR'})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="EUR">{t('capex.currencyEUR')}</option>
                    <option value="USD">{t('capex.currencyUSD')}</option>
                    <option value="TRY">{t('capex.currencyTRY')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('capex.fieldDescription')}
                </label>
                <textarea
                  value={capexFormData.description}
                  onChange={(e) => setCapexFormData({...capexFormData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder={t('capex.descriptionPlaceholder')}
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => { setShowModal(false); setShowNewCategoryInput(false); setNewCategoryName(''); }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('capex.btnCancel')}
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                {editingCapex ? t('capex.btnUpdate') : t('capex.btnSave')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && selectedCapex && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('capex.historyTitle')}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {selectedCapex.capex_number} - {selectedCapex.category}
                  </p>
                </div>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {/* CAPEX Summary */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('capex.totalBudget')}</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {getCurrencySymbol(selectedCapex.currency)}{(selectedCapex.total_amount || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('capex.used')}</p>
                    <p className="text-lg font-semibold text-orange-600">
                      {getCurrencySymbol(selectedCapex.currency)}{(selectedCapex.used_amount || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('capex.remaining')}</p>
                    <p className="text-lg font-semibold text-green-600">
                      {getCurrencySymbol(selectedCapex.currency)}{(selectedCapex.remaining_amount || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('capex.usageRate')}</p>
                    <p className="text-lg font-semibold text-primary">
                      {getUsagePercentage(selectedCapex).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Orders */}
              <div className="space-y-4 mb-8">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('capex.transactionHistory')}</h3>
                {capexOrders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <p>{t('capex.noOrders')}</p>
                  </div>
                ) : (
                  capexOrders.map((order) => (
                    <div
                      key={order.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {order.order_number}
                            </span>
                            <span className={`ml-3 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getOrderStatusColor(order.status)}`}>
                              {getOrderStatusText(order.status)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {order.description}
                          </p>
                          <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <div>
                              <span className="font-medium">{t('capex.orderFieldCompany')}</span> {order.company_name || '-'}
                            </div>
                            <div>
                              <span className="font-medium">{t('capex.orderFieldLocation')}</span> {order.location_name || '-'}
                            </div>
                            <div>
                              <span className="font-medium">{t('capex.orderFieldSupplier')}</span> {order.supplier_name || '-'}
                            </div>
                            <div>
                              <span className="font-medium">{t('capex.orderFieldCategory')}</span> {order.category || '-'}
                            </div>
                          </div>
                          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-2">
                            <span>{order.user_name || '-'}</span>
                            <span className="mx-2">•</span>
                            <span>{new Date(order.created_at).toLocaleString('tr-TR')}</span>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-lg font-semibold text-primary">
                            {getCurrencySymbol(order.currency || selectedCapex.currency)}{parseFloat(order.total_amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </p>
                          {order.invoice_amount != null && (
                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                              Fatura: {getCurrencySymbol(order.invoice_currency || order.currency)}{parseFloat(order.invoice_amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </p>
                          )}
                          {order.capex_refund_amount != null && parseFloat(order.capex_refund_amount) > 0 && (
                            <p className="text-xs text-purple-600 dark:text-purple-400 mt-1 font-medium">
                              ↩ İade: €{parseFloat(order.capex_refund_amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </p>
                          )}
                          {order.beam_order_number && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              BEAM: {order.beam_order_number}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Transaction Log */}
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">İşlem Geçmişi</h3>
                {capexTransactions.length === 0 ? (
                  <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
                    Henüz işlem kaydı bulunmuyor.
                  </div>
                ) : (
                  capexTransactions.map((tx) => {
                    const typeConfig: Record<string, { label: string; color: string; sign: string }> = {
                      block:      { label: 'Bloke',      color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', sign: '-' },
                      usage:      { label: 'Kullanım',   color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',            sign: '-' },
                      refund:     { label: 'İade',       color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',     sign: '+' },
                      adjustment: { label: 'Düzeltme',   color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',        sign: '±' },
                    };
                    const cfg = typeConfig[tx.transaction_type] || { label: tx.transaction_type, color: 'bg-gray-100 text-gray-800', sign: '' };
                    return (
                      <div key={tx.id} className="flex items-center justify-between py-2 px-3 border border-gray-100 dark:border-gray-700 rounded-lg text-sm">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className={`shrink-0 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${cfg.color}`}>
                            {cfg.label}
                          </span>
                          <div className="min-w-0">
                            <p className="text-gray-700 dark:text-gray-300 truncate">{tx.description}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              {tx.user_name || '-'} · {new Date(tx.created_at).toLocaleString('tr-TR')}
                            </p>
                          </div>
                        </div>
                        <div className={`ml-4 shrink-0 font-semibold ${tx.transaction_type === 'refund' ? 'text-green-600 dark:text-green-400' : tx.transaction_type === 'block' || tx.transaction_type === 'usage' ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
                          {cfg.sign}€{parseFloat(tx.amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CapexPage;